import React from "react";
import { Player } from "@lottiefiles/react-lottie-player";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { requireAsset } from "../../../utils/asset";
import ActivityOverview from "./ActivityOverview";

// ...existing code...
const SlideAsset = ({ src, alt = "", natural = false }) => {
  if (!src || typeof src !== "string") return null;

  const isDotLottie = /\.lottie(?:[?#]|$)/i.test(src);
  const isJsonLottie = /\.json(?:[?#]|$)/i.test(src);

  if (isDotLottie || isJsonLottie) {

    const normalized = src
      .replace(/^\/+/, "")
      .replace(/^(?:\.\.\/)*assets?\//i, "")
      .replace(/\/{2,}/g, "/");

    const resolved = /^(https?:)?\/\//i.test(src)
      ? src
      : requireAsset(normalized);

    if (isDotLottie) {
      return <DotLottieReact src={resolved} autoplay layout={{ fit: "contain", align: [0.5, 0.5] }}
        style={{ width: "100%", height: "100%" }} />;
    }

    return (
      <Player src={resolved} autoplay loop={true} className="w-full h-full" aria-label={alt} />
    );
  }

  // Natural-ratio sizing: never stretched or cropped
  return (
    <img
      src={src}
      alt={alt}
      className={`mx-auto w-auto h-auto max-w-full object-contain ${natural ? "max-h-[60vh]" : "max-h-full"}`}
    />
  );
};

const SlideHighlight = ({ children }) => {
  if (!children) return null;

  return (
    <div className="mt-8 mx-auto flex w-full max-w-2xl items-center justify-center rounded-xl border border-amber-200 bg-amber-50 px-6 py-4 text-center">
      <p className="m-0 text-lg font-semibold leading-relaxed text-amber-950">
        {children}
      </p>
    </div>
  );
};

const OnboardingSlide = ({ slide }) => {
  let data = slide?.datajson || {};

  // Support JSON strings returned by the backend.
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }

  if (!data || typeof data !== "object" || Array.isArray(data)) {
    data = {};
  }

  if (data.type === "activity_overview") {
    return <ActivityOverview data={data} />;
  }

  const hasData = Object.keys(data).length > 0;
  const heading = data.heading || slide?.activitytitle;

  // Only HTML slides explicitly opting into a layout
  // use the enhanced renderer.
  const isEnhanced =
    (data.type == null || data.type === "html") &&
    (data.layout === "top" || data.layout === "left");

  const asset =
    typeof data.asset === "string" && data.asset.trim()
      ? data.asset.trim()
      : null;

  // Convert root-relative asset paths for browser loading.
  // Example: asset/lottie/brain.json
  //      -> /asset/lottie/brain.json
  const assetUrl = asset
    ? /^(https?:)?\/\//i.test(asset) || asset.startsWith("/")
      ? asset
      : `/${asset.replace(/^\.?\//, "")}`
    : null;

  // --------------------------------------------------
  // LEGACY RENDERER
  // Preserves existing course configurations.
  // --------------------------------------------------

  if (!isEnhanced) {
    return (
      <div className="flex flex-col grow">
        <div className="flex flex-row grow sm:mx-12 sm:mt-12">
          <div className="w-full max-w-6xl mx-auto flex flex-col justify-center p-8">

            {!hasData && slide?.activitytitle != null && (
              <div className="flex flex-col justify-center w-full h-full">
                <span className="text-center text-4xl font-MW mt-1 mb-[0.8rem]">
                  {slide.activitytitle}
                </span>
              </div>
            )}

            {hasData && (
              <div className="w-full h-full flex items-center justify-center">
                <div>
                  {heading != null && (
                    <h1 className="text-4xl text-center font-MS font-extrabold mt-1 mb-[0.8rem]">
                      {heading}
                    </h1>
                  )}

                  <div className="flex flex-col grow justify-center py-6">
                    {data.text != null && ( <div className="pb-6"> {data.text} </div> )}
                    {data.html != null && ( <div dangerouslySetInnerHTML={{ __html: data.html, }} /> )}

                    {data.type === "youtube" && (
                      <iframe className="w-full max-w-4xl mx-auto aspect-video" src={data.url} title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
                    )}

                    {data.type === "image" && (
                      <img
                        className="w-full max-w-4xl mx-auto aspect-video border border-background_fade"
                        src={data.url}
                        alt={heading || ""}
                      />
                    )}

                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --------------------------------------------------
  // ENHANCED RENDERER
  // --------------------------------------------------

  const isTop = data.layout === "top";

  const content = (
    <div className={isTop ? "w-full text-center" : "w-full text-left"}>

      {heading && (
        <h1 className="font-MS text-3xl lg:text-4xl font-extrabold leading-tight tracking-tight text-slate-900">
          {heading}
        </h1>
      )}

      {data.text != null && (
        <div className="mt-5 text-lg lg:text-xl leading-relaxed text-slate-600">
          {data.text}
        </div>
      )}

      {data.html != null && (
        <div
          className={[
            "mt-5 text-lg lg:text-xl leading-relaxed text-slate-600",
            "[&_h1]:text-4xl [&_h1]:font-extrabold [&_h1]:mb-5",
            "[&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mb-5",
            "[&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:mb-4",
            "[&_p]:mb-4",
            "[&_ul]:list-disc [&_ul]:pl-6",
            "[&_ol]:list-decimal [&_ol]:pl-6",
          ].join(" ")}
          dangerouslySetInnerHTML={{
            __html: data.html,
          }}
        />
      )}

      <SlideHighlight>
        {data.highlight}
      </SlideHighlight>

    </div>
  );

  return (
    <div className="flex flex-col grow bg-[#FAFAF8]">
      <div className="flex grow items-center justify-center px-6 py-8 sm:px-12">

        <div className="w-full max-w-6xl mx-auto">

          {isTop ? (
            <div className="flex flex-col items-center justify-center">
              {assetUrl && (
                <div className="mb-7 h-full max-h-xl">
                  <SlideAsset
                    src={assetUrl}
                    alt={heading || ""}
                  />
                </div>
              )}

              <div className="w-full max-w-3xl">
                {content}
              </div>

            </div>
          ) : (
            <div className="grid grid-cols-2 items-center gap-10 lg:gap-14">

              {assetUrl && (
                <div className="flex items-center justify-center">
                  <div className={`w-full max-w-xl ${/\.(lottie|json)(?:[?#]|$)/i.test(assetUrl) ? "aspect-square max-h-[60vh]" : ""}`}>
                    <SlideAsset src={assetUrl} alt={heading || ""} natural />
                  </div>
                </div>
              )}

              <div className={assetUrl ? "" : "lg:col-span-2"}>
                {content}
              </div>

            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default OnboardingSlide;
